// src/crm/CrmCreatorsPage.tsx
import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  useReactTable,
  type SortingState,
} from '@tanstack/react-table'
import type { ContactType, Creator } from '../lib/types'
import { useCreators, useDeleteCreator } from './api'
import { ContactTypeBadge, CONTACT_TYPE_LABELS } from './components/ContactTypeBadge'
import { PlatformBadge } from './components/PlatformBadge'

const col = createColumnHelper<Creator>()

const COLUMNS = [
  col.accessor('displayName', {
    header: 'Nom',
    cell: (info) => (
      <span style={{ color: 'var(--fg)', fontWeight: 500 }}>{info.getValue()}</span>
    ),
    enableSorting: true,
  }),
  col.accessor('contactType', {
    header: 'Type',
    cell: (info) => <ContactTypeBadge type={info.getValue()} />,
    enableSorting: true,
  }),
  col.accessor('organization', {
    header: 'Organisation',
    cell: (info) => (
      <span style={{ color: 'var(--muted)' }}>{info.getValue() ?? '—'}</span>
    ),
    enableSorting: true,
  }),
  col.accessor('niche', {
    header: 'Niche',
    cell: (info) => (
      <span style={{ color: 'var(--muted)' }}>{info.getValue() ?? '—'}</span>
    ),
    enableSorting: false,
  }),
  col.accessor('socials', {
    header: 'Plateformes',
    cell: (info) => {
      const socials = info.getValue()
      if (!socials || socials.length === 0) {
        return <span style={{ color: 'var(--dim)' }}>—</span>
      }
      return (
        <div>
          {socials.map((s) => (
            <PlatformBadge key={s.id} platform={s.platform} />
          ))}
        </div>
      )
    },
    enableSorting: false,
  }),
  col.accessor('source', {
    header: 'Source',
    cell: (info) => (
      <span style={{ color: 'var(--muted)', fontSize: '0.8125rem' }}>
        {info.getValue()}
      </span>
    ),
    enableSorting: true,
  }),
]

const th: React.CSSProperties = {
  padding: '0.5rem 0.75rem',
  textAlign: 'left',
  fontSize: '0.75rem',
  fontWeight: 600,
  color: 'var(--dim)',
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  borderBottom: '1px solid var(--line)',
  whiteSpace: 'nowrap',
  userSelect: 'none',
}

const td: React.CSSProperties = {
  padding: '0.625rem 0.75rem',
  fontSize: '0.875rem',
  borderBottom: '1px solid var(--line)',
  verticalAlign: 'middle',
}

export function CrmCreatorsPage() {
  const navigate = useNavigate()
  const [query, setQuery] = useState('')
  const [sorting, setSorting] = useState<SortingState>([])
  const [globalFilter, setGlobalFilter] = useState('')
  const [typeFilter, setTypeFilter] = useState<ContactType | ''>('')
  const [nicheFilter, setNicheFilter] = useState('')
  const [countryFilter, setCountryFilter] = useState('')

  // States bruts (jamais `undefined`) : exactOptionalPropertyTypes est activé,
  // et useCreators ignore déjà les chaînes vides.
  const { data: creators = [], isLoading, isError } = useCreators(query || undefined, {
    type: typeFilter,
    niche: nicheFilter,
    country: countryFilter,
  })
  const deleteCreator = useDeleteCreator()

  const table = useReactTable({
    data: creators,
    columns: COLUMNS,
    state: { sorting, globalFilter },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    globalFilterFn: 'includesString',
  })

  // Filtre nom via l'API (debounced via query state) ET filtre client via globalFilter
  const debouncedSetQuery = useMemo(() => {
    let timer: ReturnType<typeof setTimeout>
    return (val: string) => {
      clearTimeout(timer)
      timer = setTimeout(() => setQuery(val), 350)
    }
  }, [])

  function handleSearch(e: React.ChangeEvent<HTMLInputElement>) {
    const val = e.target.value
    setGlobalFilter(val)
    debouncedSetQuery(val)
  }

  function handleDelete(id: string, name: string) {
    if (!confirm(`Supprimer le contact "${name}" ? Cette action est irréversible.`)) return
    deleteCreator.mutate(id, { onError: (err) => alert(err.message) })
  }

  return (
    <div style={{ padding: '2rem 2.5rem' }}>
      {/* En-tête */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
        <h1 style={{
          fontSize: '1.5rem',
          fontWeight: 600,
          color: 'var(--fg)',
          margin: 0,
          letterSpacing: '-0.025em',
        }}>
          Contacts
        </h1>
        <button
          onClick={() => navigate('/crm/contacts/new')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.375rem',
            background: 'var(--arancio)',
            color: '#fff',
            border: 'none',
            borderRadius: '7px',
            padding: '0.4375rem 0.875rem',
            fontSize: '0.875rem',
            fontWeight: 600,
            cursor: 'pointer',
            transition: 'background 0.12s',
          }}
          onMouseOver={(e) => { e.currentTarget.style.background = 'var(--arancio-deep)' }}
          onMouseOut={(e) => { e.currentTarget.style.background = 'var(--arancio)' }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
            <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
          </svg>
          Nouveau contact
        </button>
      </div>

      {/* Onglets par type */}
      <div style={{ display: 'flex', gap: '0.375rem', marginBottom: '1rem' }}>
        {([['', 'Tous'], ...Object.entries(CONTACT_TYPE_LABELS)] as ['' | ContactType, string][]).map(
          ([value, label]) => (
            <button
              key={value || 'all'}
              onClick={() => setTypeFilter(value)}
              style={{
                background: typeFilter === value ? 'var(--elevated)' : 'transparent',
                border: '1px solid',
                borderColor: typeFilter === value ? 'var(--line-strong)' : 'var(--line)',
                borderRadius: '7px',
                padding: '0.3125rem 0.75rem',
                color: typeFilter === value ? 'var(--fg)' : 'var(--muted)',
                fontSize: '0.8125rem',
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              {label}
            </button>
          ),
        )}
      </div>

      {/* Barre de recherche et filtres */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
        <input
          type="search"
          placeholder="Filtrer par nom..."
          value={globalFilter}
          onChange={handleSearch}
          style={{
            width: '100%',
            maxWidth: '320px',
            background: 'var(--elevated)',
            border: '1px solid var(--line-strong)',
            borderRadius: '7px',
            padding: '0.4375rem 0.75rem',
            color: 'var(--fg)',
            fontSize: '0.875rem',
            outline: 'none',
          }}
        />
        <input
          type="search"
          placeholder="Niche…"
          value={nicheFilter}
          onChange={(e) => setNicheFilter(e.target.value)}
          style={{
            width: '100%',
            maxWidth: '160px',
            background: 'var(--elevated)',
            border: '1px solid var(--line-strong)',
            borderRadius: '7px',
            padding: '0.4375rem 0.75rem',
            color: 'var(--fg)',
            fontSize: '0.875rem',
            outline: 'none',
          }}
        />
        <input
          type="search"
          placeholder="Pays (FR…)"
          value={countryFilter}
          onChange={(e) => setCountryFilter(e.target.value.toUpperCase())}
          maxLength={2}
          style={{
            width: '100%',
            maxWidth: '110px',
            background: 'var(--elevated)',
            border: '1px solid var(--line-strong)',
            borderRadius: '7px',
            padding: '0.4375rem 0.75rem',
            color: 'var(--fg)',
            fontSize: '0.875rem',
            outline: 'none',
          }}
        />
      </div>

      {/* États */}
      {isLoading && (
        <p style={{ color: 'var(--muted)', padding: '2rem 0' }}>Chargement…</p>
      )}
      {isError && (
        <p style={{ color: 'var(--arancio)', padding: '2rem 0' }}>
          Erreur lors du chargement des contacts.
        </p>
      )}

      {/* Table */}
      {!isLoading && !isError && (
        <div style={{
          background: 'var(--surface)',
          border: '1px solid var(--line)',
          borderRadius: '10px',
          overflow: 'hidden',
        }}>
          {creators.length === 0 ? (
            <div style={{
              padding: '3rem 2rem',
              textAlign: 'center',
              color: 'var(--muted)',
            }}>
              <p style={{ margin: 0 }}>Aucun contact.</p>
              <p style={{ margin: '0.5rem 0 0', fontSize: '0.8125rem', color: 'var(--dim)' }}>
                Ajoutez votre premier contact avec le bouton "Nouveau".
              </p>
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                {table.getHeaderGroups().map((hg) => (
                  <tr key={hg.id}>
                    {hg.headers.map((header) => (
                      <th
                        key={header.id}
                        style={{
                          ...th,
                          cursor: header.column.getCanSort() ? 'pointer' : 'default',
                        }}
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {header.column.getIsSorted() === 'asc' && ' ↑'}
                        {header.column.getIsSorted() === 'desc' && ' ↓'}
                      </th>
                    ))}
                    <th style={th}>Actions</th>
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map((row) => (
                  <tr
                    key={row.id}
                    style={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/crm/contacts/${row.original.id}`)}
                    onMouseOver={(e) => { (e.currentTarget as HTMLTableRowElement).style.background = 'var(--elevated)' }}
                    onMouseOut={(e) => { (e.currentTarget as HTMLTableRowElement).style.background = '' }}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} style={td}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                    <td style={td} onClick={(e) => e.stopPropagation()}>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                          onClick={() => navigate(`/crm/contacts/${row.original.id}/edit`)}
                          style={{
                            background: 'var(--elevated)',
                            border: '1px solid var(--line)',
                            borderRadius: '5px',
                            padding: '0.25rem 0.5rem',
                            color: 'var(--muted)',
                            fontSize: '0.75rem',
                            cursor: 'pointer',
                          }}
                        >
                          Modifier
                        </button>
                        <button
                          onClick={() => handleDelete(row.original.id, row.original.displayName)}
                          style={{
                            background: 'rgba(217,83,30,0.12)',
                            border: '1px solid rgba(217,83,30,0.25)',
                            borderRadius: '5px',
                            padding: '0.25rem 0.5rem',
                            color: 'var(--arancio)',
                            fontSize: '0.75rem',
                            cursor: 'pointer',
                          }}
                        >
                          Supprimer
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Compteur */}
      {!isLoading && !isError && creators.length > 0 && (
        <p style={{ color: 'var(--dim)', fontSize: '0.8125rem', marginTop: '0.75rem' }}>
          {table.getFilteredRowModel().rows.length} contact
          {table.getFilteredRowModel().rows.length > 1 ? 's' : ''}
        </p>
      )}
    </div>
  )
}
